/**
 * Results Dashboard Store
 *
 * Zustand store for managing dashboard state including chart configurations,
 * selected variables, and unit preferences.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { OutputVariable } from '@/simulation/parsers/types';
import type { UnitSystem } from './utils/unitConversion';
import type { AggregationFrequency } from './utils/aggregation';

/**
 * Chart type options
 */
export type ChartType = 'line' | 'bar' | 'area' | 'stacked-area';

/**
 * Chart configuration
 */
export interface ChartConfig {
  id: string;
  title: string;
  type: ChartType;
  frequency: AggregationFrequency;
  variableIds: number[];
  colors: Record<number, string>;
}

/**
 * Dashboard view mode
 */
export type ViewMode = 'dashboard' | 'table' | 'compare';

/**
 * Results state
 */
interface ResultsState {
  // Unit preference
  unitSystem: UnitSystem;

  // View mode
  viewMode: ViewMode;

  // Building area for EUI calculation
  buildingArea: number;

  // Selected variables for charting
  selectedVariables: OutputVariable[];

  // Custom chart configurations
  charts: ChartConfig[];

  // Active chart ID (for editing)
  activeChartId: string | null;

  // Aggregation frequency preference
  defaultFrequency: AggregationFrequency;
}

/**
 * Results actions
 */
interface ResultsActions {
  // Unit system
  setUnitSystem: (system: UnitSystem) => void;
  toggleUnitSystem: () => void;

  // View mode
  setViewMode: (mode: ViewMode) => void;

  // Building area
  setBuildingArea: (area: number) => void;

  // Variable selection
  selectVariable: (variable: OutputVariable) => void;
  deselectVariable: (variableId: number) => void;
  toggleVariable: (variable: OutputVariable) => void;
  clearSelectedVariables: () => void;
  setSelectedVariables: (variables: OutputVariable[]) => void;

  // Chart management
  addChart: (config: Omit<ChartConfig, 'id'>) => string;
  updateChart: (id: string, updates: Partial<ChartConfig>) => void;
  removeChart: (id: string) => void;
  setActiveChart: (id: string | null) => void;

  // Default frequency
  setDefaultFrequency: (frequency: AggregationFrequency) => void;

  // Reset
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: ResultsState = {
  unitSystem: 'SI',
  viewMode: 'dashboard',
  buildingArea: 0,
  selectedVariables: [],
  charts: [],
  activeChartId: null,
  defaultFrequency: 'monthly',
};

/**
 * Generate unique chart ID
 */
function generateChartId(): string {
  return `chart-${String(Date.now())}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Default colors for chart series
 */
const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#22c55e', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
];

/**
 * Results store
 */
export const useResultsStore = create<ResultsState & ResultsActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Unit system
        setUnitSystem: (system) => set({ unitSystem: system }),
        toggleUnitSystem: () =>
          set({ unitSystem: get().unitSystem === 'SI' ? 'IP' : 'SI' }),

        // View mode
        setViewMode: (mode) => set({ viewMode: mode }),

        // Building area
        setBuildingArea: (area) => set({ buildingArea: area }),

        // Variable selection
        selectVariable: (variable) => {
          const current = get().selectedVariables;
          if (!current.find((v) => v.id === variable.id)) {
            set({ selectedVariables: [...current, variable] });
          }
        },

        deselectVariable: (variableId) => {
          set({
            selectedVariables: get().selectedVariables.filter(
              (v) => v.id !== variableId
            ),
          });
        },

        toggleVariable: (variable) => {
          const current = get().selectedVariables;
          const exists = current.find((v) => v.id === variable.id);
          if (exists) {
            set({ selectedVariables: current.filter((v) => v.id !== variable.id) });
          } else {
            set({ selectedVariables: [...current, variable] });
          }
        },

        clearSelectedVariables: () => set({ selectedVariables: [] }),

        setSelectedVariables: (variables) => set({ selectedVariables: variables }),

        // Chart management
        addChart: (config) => {
          const id = generateChartId();
          const newChart: ChartConfig = {
            ...config,
            id,
            colors: config.variableIds.reduce(
              (acc, varId, index) => ({
                ...acc,
                [varId]: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
              }),
              {}
            ),
          };
          set({ charts: [...get().charts, newChart], activeChartId: id });
          return id;
        },

        updateChart: (id, updates) => {
          set({
            charts: get().charts.map((chart) =>
              chart.id === id ? { ...chart, ...updates } : chart
            ),
          });
        },

        removeChart: (id) => {
          const state = get();
          set({
            charts: state.charts.filter((c) => c.id !== id),
            activeChartId: state.activeChartId === id ? null : state.activeChartId,
          });
        },

        setActiveChart: (id) => set({ activeChartId: id }),

        // Default frequency
        setDefaultFrequency: (frequency) => set({ defaultFrequency: frequency }),

        // Reset
        reset: () => set(initialState),
      }),
      {
        name: 'results-store',
        partialize: (state) => ({
          unitSystem: state.unitSystem,
          buildingArea: state.buildingArea,
          defaultFrequency: state.defaultFrequency,
        }),
      }
    ),
    { name: 'results-store' }
  )
);

/**
 * Selectors
 */
export const selectUnitSystem = (state: ResultsState) => state.unitSystem;
export const selectViewMode = (state: ResultsState) => state.viewMode;
export const selectSelectedVariables = (state: ResultsState) => state.selectedVariables;
export const selectCharts = (state: ResultsState) => state.charts;
export const selectActiveChartId = (state: ResultsState) => state.activeChartId;
